import { useMemo, useState } from "react";
import { Bars3Icon } from "@heroicons/react/24/solid";
import Select from "../../../components/input/Select";
import { AdminImportStudent } from "../rosterImportApiSlice";
import { formatMatchIdentification, formatStudentInfo, MergeItem } from "./conflictReviewUtils";

type DragZone = "create" | "merge";

interface ConfirmStepProps {
	createNew: AdminImportStudent[];
	merges: MergeItem[];
	updateSelectionsWithCreateNew: (row: number) => void;
	updateSelectionsWithMerge: (row: number, matchId: number) => void;
}

const rowMime = "text/row";

const zoneClass = (isActive: boolean) =>
	`rounded-lg border bg-gray-50 p-2 ${isActive ? "border-primary-600 ring-2 ring-primary-600" : "border-gray-200"}`;

const cardClass =
	"cursor-grab active:cursor-grabbing rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900";

export function ConfirmStep({
	createNew,
	merges,
	updateSelectionsWithCreateNew,
	updateSelectionsWithMerge,
}: ConfirmStepProps) {
	const [dragOver, setDragOver] = useState<DragZone | null>(null);
	const [pendingMergeStudent, setPendingMergeStudent] = useState<AdminImportStudent | null>(null);
	const [draggingRow, setDraggingRow] = useState<number | null>(null);
	const [dragSource, setDragSource] = useState<DragZone | null>(null);

	const mergePickerOptions = useMemo(() => {
		if (!pendingMergeStudent) return [];

		return [
			{ value: "", label: "Select existing user..." },
			...pendingMergeStudent.identifications.map((m) => ({
				value: String(m.id),
				label: `Merge with: ${formatMatchIdentification(m)}`,
			})),
		];
	}, [pendingMergeStudent]);

	const setDragData = (e: React.DragEvent, row: number, source: DragZone) => {
		e.dataTransfer.setData(rowMime, String(row));
		e.dataTransfer.effectAllowed = "move";
		setDraggingRow(row);
		setDragSource(source);
	};

	const readDragRow = (e: React.DragEvent) => {
		const raw = e.dataTransfer.getData(rowMime);
		const row = Number(raw);
		return Number.isFinite(row) ? row : null;
	};

	const allowDrop = (e: React.DragEvent, zone: DragZone) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		setDragOver(zone);
	};

	const dropToCreate = (e: React.DragEvent) => {
		e.preventDefault();
		setDragOver(null);
		setDraggingRow(null);

		const row = readDragRow(e);
		if (row == null) return;

		setPendingMergeStudent(null);
		updateSelectionsWithCreateNew(row);
		setDragSource(null);
	};

	const dropToMerge = (e: React.DragEvent) => {
		e.preventDefault();
		setDragOver(null);
		setDraggingRow(null);

		const row = readDragRow(e);
		if (row == null) return;

		if (dragSource !== "create") {
			setDragSource(null);
			return;
		}

		const student = findStudentByRow(row);

		if (!student?.identifications?.length) {
			setDragSource(null);
			return;
		}

		setPendingMergeStudent(student);
		setDragSource(null);
	};

	const findStudentByRow = (row: number) =>
		createNew.find((s) => s.row === row) ?? merges.find((m) => m.student.row === row)?.student;

	const cancelPendingMerge = () => setPendingMergeStudent(null);

	const confirmPendingMerge = (matchId: string) => {
		if (!pendingMergeStudent) return;
		if (!matchId) return;

		updateSelectionsWithMerge(pendingMergeStudent.row, Number(matchId));
		setPendingMergeStudent(null);
	};

	return (
		<div className="space-y-4">
			<div>
				<h2 className="text-sm font-semibold">Confirm Your Choices</h2>
				<p className="text-sm text-gray-600">
					Review what accounts will be created vs merged before continuing the import. You
					can drag students between columns to change your choice. Dropping into{" "}
					<span className="font-semibold">Merge</span> will ask you to pick a match.
				</p>
			</div>
			{pendingMergeStudent && (
				<div className="rounded-lg border border-primary-200 bg-primary-50 p-2">
					<h2 className="text-sm">
						{pendingMergeStudent.lName}, {pendingMergeStudent.fName}
					</h2>
					<div className="text-xs text-gray-600">
						{formatStudentInfo(pendingMergeStudent, false)}
					</div>
					<div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
						<Select
							value=""
							setValue={(v) => {
								confirmPendingMerge(v as string);
							}}
							simpleListBoxId={`merge-picker-${pendingMergeStudent.row}`}
							options={mergePickerOptions}
							label="Merge With"
							fieldClasses="w-full"
						/>
						<button
							type="button"
							className="btn btn-neutral"
							onClick={cancelPendingMerge}>
							Cancel
						</button>
					</div>
				</div>
			)}
			<div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
				<div
					className={zoneClass(dragOver === "create")}
					onDragEnter={(e) => allowDrop(e, "create")}
					onDragOver={(e) => allowDrop(e, "create")}
					onDragLeave={() => setDragOver(null)}
					onDrop={dropToCreate}>
					<h3 className="text-sm text-gray-900">Create New Users ({createNew.length})</h3>
					<div className="mt-2 max-h-56 overflow-y-auto space-y-2">
						{createNew.length === 0
							? null
							: createNew.map((s) => (
									<div
										key={s.row}
										draggable
										onDragStart={(e) => setDragData(e, s.row, "create")}
										onDragEnd={() => {
											setDraggingRow(null);
											setDragSource(null);
										}}
										className={cardClass}
										title="Drag to Merge">
										<div className="font-medium flex justify-between items-center">
											{formatStudentInfo(s, true)}
											<Bars3Icon
												width={20}
												className={
													draggingRow === s.row
														? "text-primary-700"
														: "text-gray-600"
												}
											/>
										</div>
									</div>
								))}
					</div>
				</div>
				<div
					className={zoneClass(dragOver === "merge")}
					onDragEnter={(e) => allowDrop(e, "merge")}
					onDragOver={(e) => allowDrop(e, "merge")}
					onDragLeave={() => setDragOver(null)}
					onDrop={dropToMerge}>
					<h3 className="text-sm text-gray-900">
						Merge with Existing Users ({merges.length})
					</h3>
					<div className="mt-2 max-h-56 overflow-y-auto space-y-2">
						{merges.length === 0
							? null
							: merges.map(({ student, match }) => {
									const canChangeTarget =
										(student.identifications?.length ?? 0) > 1;

									return (
										<div
											key={student.row}
											draggable
											onDragStart={(e) =>
												setDragData(e, student.row, "merge")
											}
											onDragEnd={() => {
												setDraggingRow(null);
												setDragSource(null);
											}}
											className={`${cardClass} flex justify-between items-center`}
											title="Drag back to Create New">
											<div>
												<div className="font-medium">
													{formatStudentInfo(student, true)}
												</div>
												<div className="text-gray-600">
													Merge with: {formatMatchIdentification(match)}
												</div>
												{canChangeTarget && (
													<button
														type="button"
														className="text-xs text-primary-700 hover:underline"
														onClick={() =>
															setPendingMergeStudent(student)
														}>
														Change merge target
													</button>
												)}
											</div>
											<Bars3Icon
												width={20}
												className={
													draggingRow === student.row
														? "text-primary-700"
														: "text-gray-600"
												}
											/>
										</div>
									);
								})}
					</div>
				</div>
			</div>
		</div>
	);
}
